/**
 * Central traffic attribution classification for EnTa.
 * Pure helpers — no DOM/storage side effects (except optional helpers that accept injected state).
 */

export type AttributionChannel =
  | 'direct'
  | 'organic_search'
  | 'paid_search'
  | 'organic_social'
  | 'paid_social'
  | 'referral'
  | 'email'
  | 'other';

export type AttributionConfidence = 'high' | 'uncertain';

export type AttributionInput = {
  referrerHost?: string | null;
  externalReferrer?: string | null;
  landingPath?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  fbclid?: string | null;
  /** When true, Google referrer without paid markers is marked uncertain (historical backfill). */
  historicalAmbiguous?: boolean;
};

export type ClassifiedAttribution = {
  channel: AttributionChannel;
  source: string;
  medium: string;
  campaign: string | null;
  confidence: AttributionConfidence;
};

export type TouchAttribution = {
  source: string;
  medium: string;
  channel: AttributionChannel;
  campaign: string | null;
  landingPage?: string | null;
  referrer?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  at?: string;
};

export type VisitorTouchState = {
  first: TouchAttribution;
  lastNonDirect: TouchAttribution | null;
  firstSeenAt: string;
};

/** Own site + payment/ERP providers that must not become acquisition sources. */
export const SELF_REFERRAL_HOSTS: readonly string[] = [
  'festutrustning.se',
  'www.festutrustning.se',
  'checkout.stripe.com',
  'billing.stripe.com',
  'pay.stripe.com',
  'js.stripe.com',
  'hooks.stripe.com',
  'apps.fortnox.se',
  'fortnox.se',
  'www.fortnox.se',
];

const SEARCH_HOSTS: Record<string, string> = {
  'google.com': 'google',
  'www.google.com': 'google',
  'google.se': 'google',
  'www.google.se': 'google',
  'bing.com': 'bing',
  'www.bing.com': 'bing',
  'duckduckgo.com': 'duckduckgo',
  'www.duckduckgo.com': 'duckduckgo',
  'yahoo.com': 'yahoo',
  'search.yahoo.com': 'yahoo',
  'ecosia.org': 'ecosia',
  'www.ecosia.org': 'ecosia',
};

const SOCIAL_HOSTS: Record<string, string> = {
  'facebook.com': 'facebook',
  'www.facebook.com': 'facebook',
  'm.facebook.com': 'facebook',
  'lm.facebook.com': 'facebook',
  'l.facebook.com': 'facebook',
  'fb.com': 'facebook',
  'instagram.com': 'instagram',
  'www.instagram.com': 'instagram',
  'l.instagram.com': 'instagram',
  'linkedin.com': 'linkedin',
  'www.linkedin.com': 'linkedin',
  'lnkd.in': 'linkedin',
  'twitter.com': 'twitter',
  'www.twitter.com': 'twitter',
  'x.com': 'twitter',
  't.co': 'twitter',
  'tiktok.com': 'tiktok',
  'www.tiktok.com': 'tiktok',
  'youtube.com': 'youtube',
  'www.youtube.com': 'youtube',
  'm.youtube.com': 'youtube',
  'youtu.be': 'youtube',
  'pinterest.com': 'pinterest',
  'www.pinterest.com': 'pinterest',
  'snapchat.com': 'snapchat',
  'www.snapchat.com': 'snapchat',
};

const CHANNEL_LABELS: Record<AttributionChannel, string> = {
  direct: 'Direkt / okänd',
  organic_search: 'Organic Search',
  paid_search: 'Paid Search',
  organic_social: 'Organic Social',
  paid_social: 'Paid Social',
  referral: 'Referral',
  email: 'Email',
  other: 'Other',
};

const PAID_MEDIUMS = new Set([
  'cpc',
  'ppc',
  'paid',
  'paidsearch',
  'paid_search',
  'paid-social',
  'paid_social',
  'paidsocial',
  'display',
  'retargeting',
  'remarketing',
  'ads',
  'ad',
  'cpm',
  'cpa',
]);

const EMAIL_MEDIUMS = new Set(['email', 'e-mail', 'newsletter', 'mail']);
const SOCIAL_MEDIUMS = new Set([
  'social',
  'organic_social',
  'social-organic',
  'social_organic',
]);
const ORGANIC_MEDIUMS = new Set(['organic', 'organic_search']);

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function stripWww(host: string): string {
  return host.replace(/^www\./i, '');
}

/** True if host is our site or a payment/tech self-referral. */
export function shouldIgnoreReferrer(host: string | null | undefined): boolean {
  const h = norm(host);
  if (!h) return false;
  if (SELF_REFERRAL_HOSTS.some((ex) => h === ex || h.endsWith(`.${ex}`))) return true;
  // Any stripe.com subdomain (checkout, pay, …)
  if (h === 'stripe.com' || h.endsWith('.stripe.com')) return true;
  // Fortnox ERP / invoice portals
  if (h === 'fortnox.se' || h.endsWith('.fortnox.se')) return true;
  // Own apex without listing every subdomain
  if (h === 'festutrustning.se' || h.endsWith('.festutrustning.se')) return true;
  return false;
}

export function isDirectChannel(channel: AttributionChannel | string | null | undefined): boolean {
  return channel === 'direct';
}

export function channelDisplayLabel(channel: AttributionChannel | string): string {
  if (channel in CHANNEL_LABELS) {
    return CHANNEL_LABELS[channel as AttributionChannel];
  }
  return channel;
}

export function sourceDisplayLabel(
  channel: AttributionChannel,
  source: string,
  options?: { uncertain?: boolean }
): string {
  const s = source || '(unknown)';
  if (channel === 'paid_search' && s === 'google') return 'Google Ads';
  if (channel === 'organic_search' && s === 'google') {
    return options?.uncertain ? 'Google (osäker: saknar Ads-signal)' : 'Google Organic';
  }
  if (channel === 'paid_social') {
    if (s === 'facebook' || s === 'meta') return 'Facebook Ads';
    if (s === 'instagram') return 'Instagram Ads';
  }
  if (channel === 'organic_social') {
    if (s === 'facebook' || s === 'meta') return 'Facebook Organic';
    if (s === 'instagram') return 'Instagram Organic';
  }
  if (channel === 'direct') return 'Direkt / okänd';
  return s;
}

function resolveSearchSource(host: string): string | null {
  const h = norm(host);
  if (SEARCH_HOSTS[h]) return SEARCH_HOSTS[h];
  // google.xx / google.co.uk etc.
  if (/^(www\.)?google\.[a-z.]+$/.test(h)) return 'google';
  return null;
}

function resolveSocialSource(host: string): string | null {
  const h = norm(host);
  if (SOCIAL_HOSTS[h]) return SOCIAL_HOSTS[h];
  return null;
}

function hasGoogleClickId(input: AttributionInput): boolean {
  return Boolean(norm(input.gclid) || norm(input.gbraid) || norm(input.wbraid));
}

function hasMetaClickId(input: AttributionInput): boolean {
  return Boolean(norm(input.fbclid));
}

function isPaidMedium(medium: string): boolean {
  return PAID_MEDIUMS.has(medium);
}

function normalizeUtmSource(raw: string): string {
  const s = norm(raw);
  if (!s) return s;
  if (['google', 'googleads', 'adwords', 'google_ads'].includes(s)) return 'google';
  if (['facebook', 'fb', 'meta', 'facebook.com', 'fb.com'].includes(s)) return 'facebook';
  if (['instagram', 'ig', 'instagram.com'].includes(s)) return 'instagram';
  if (['bing', 'microsoft', 'msn', 'bingads'].includes(s)) return 'bing';
  if (['email', 'newsletter', 'mailchimp', 'klaviyo'].includes(s)) return s === 'newsletter' ? 'newsletter' : 'email';
  return s;
}

function inferSourceFromUtm(utmSource: string, utmMedium: string): string {
  const src = normalizeUtmSource(utmSource);
  if (src) return src;
  if (isPaidMedium(utmMedium) || ORGANIC_MEDIUMS.has(utmMedium)) return 'unknown';
  return 'unknown';
}

function isMetaSource(source: string): boolean {
  return ['facebook', 'instagram', 'meta', 'fb'].includes(source);
}

function isGoogleSource(source: string): boolean {
  return source === 'google';
}

/**
 * Classify raw attribution signals into a channel/source/medium.
 * UTM + click IDs take priority over referrer when present and clear.
 */
export function classifyAttribution(input: AttributionInput): ClassifiedAttribution {
  const utmSourceRaw = norm(input.utmSource);
  const utmMedium = norm(input.utmMedium);
  const utmCampaign = (input.utmCampaign ?? '').trim() || null;
  const hostRaw = norm(input.referrerHost);
  const host = shouldIgnoreReferrer(hostRaw) ? '' : hostRaw;

  const hasUtm = Boolean(utmSourceRaw || utmMedium || utmCampaign);
  const googleClick = hasGoogleClickId(input);
  const metaClick = hasMetaClickId(input);

  // --- Paid Search via click IDs ---
  if (googleClick) {
    return {
      channel: 'paid_search',
      source: 'google',
      medium: utmMedium || 'cpc',
      campaign: utmCampaign,
      confidence: 'high',
    };
  }

  // --- UTM-driven classification ---
  if (hasUtm) {
    const source = inferSourceFromUtm(utmSourceRaw, utmMedium);

    if (EMAIL_MEDIUMS.has(utmMedium) || source === 'email' || source === 'newsletter') {
      return {
        channel: 'email',
        source: source === 'unknown' ? 'email' : source,
        medium: utmMedium || 'email',
        campaign: utmCampaign,
        confidence: 'high',
      };
    }

    if (isPaidMedium(utmMedium) || utmMedium.includes('paid')) {
      if (isGoogleSource(source) || source === 'bing') {
        return {
          channel: 'paid_search',
          source: source === 'unknown' ? 'google' : source,
          medium: utmMedium || 'cpc',
          campaign: utmCampaign,
          confidence: 'high',
        };
      }
      if (isMetaSource(source) || SOCIAL_MEDIUMS.has(utmMedium) || utmMedium.includes('social')) {
        return {
          channel: 'paid_social',
          source: source === 'meta' ? 'facebook' : source === 'unknown' ? 'facebook' : source,
          medium: utmMedium || 'paid_social',
          campaign: utmCampaign,
          confidence: 'high',
        };
      }
      // Generic paid
      if (isMetaSource(source)) {
        return {
          channel: 'paid_social',
          source,
          medium: utmMedium,
          campaign: utmCampaign,
          confidence: 'high',
        };
      }
      return {
        channel: 'other',
        source: source === 'unknown' ? utmSourceRaw || 'paid' : source,
        medium: utmMedium || 'paid',
        campaign: utmCampaign,
        confidence: 'high',
      };
    }

    if (ORGANIC_MEDIUMS.has(utmMedium)) {
      return {
        channel: 'organic_search',
        source: source === 'unknown' ? 'google' : source,
        medium: 'organic',
        campaign: utmCampaign,
        confidence: 'high',
      };
    }

    if (SOCIAL_MEDIUMS.has(utmMedium) || isMetaSource(source)) {
      // fbclid without paid medium still leans paid if click id present
      if (metaClick || isPaidMedium(utmMedium)) {
        return {
          channel: 'paid_social',
          source: source === 'meta' ? 'facebook' : source === 'unknown' ? 'facebook' : source,
          medium: utmMedium || 'paid_social',
          campaign: utmCampaign,
          confidence: 'high',
        };
      }
      return {
        channel: 'organic_social',
        source: source === 'meta' ? 'facebook' : source === 'unknown' ? 'facebook' : source,
        medium: utmMedium || 'social',
        campaign: utmCampaign,
        confidence: 'high',
      };
    }

    if (utmMedium === 'referral') {
      return {
        channel: 'referral',
        source: source === 'unknown' ? utmSourceRaw || 'referral' : source,
        medium: 'referral',
        campaign: utmCampaign,
        confidence: 'high',
      };
    }

    // UTM present but ambiguous medium
    if (isGoogleSource(source)) {
      return {
        channel: 'other',
        source: 'google',
        medium: utmMedium || '(none)',
        campaign: utmCampaign,
        confidence: 'high',
      };
    }

    return {
      channel: 'other',
      source: source === 'unknown' ? utmSourceRaw || 'other' : source,
      medium: utmMedium || '(none)',
      campaign: utmCampaign,
      confidence: 'high',
    };
  }

  // --- Click IDs without UTM ---
  if (metaClick) {
    const socialFromHost = host ? resolveSocialSource(host) : null;
    return {
      channel: 'paid_social',
      source: socialFromHost || 'facebook',
      medium: 'paid_social',
      campaign: null,
      confidence: 'high',
    };
  }

  // --- Referrer-only ---
  if (host) {
    const searchSource = resolveSearchSource(host);
    if (searchSource) {
      const uncertain = Boolean(input.historicalAmbiguous) && searchSource === 'google';
      return {
        channel: 'organic_search',
        source: searchSource,
        medium: 'organic',
        campaign: null,
        confidence: uncertain ? 'uncertain' : 'high',
      };
    }

    const socialSource = resolveSocialSource(host);
    if (socialSource) {
      return {
        channel: 'organic_social',
        source: socialSource,
        medium: 'social',
        campaign: null,
        confidence: 'high',
      };
    }

    return {
      channel: 'referral',
      source: stripWww(host) || host,
      medium: 'referral',
      campaign: null,
      confidence: 'high',
    };
  }

  // Self-referral with no other signals → Direct
  return {
    channel: 'direct',
    source: '(direct)',
    medium: '(none)',
    campaign: null,
    confidence: 'high',
  };
}

/** Apply a new session classification onto visitor first/last-non-direct state. */
export function mergeVisitorTouch(
  existing: VisitorTouchState | null,
  session: ClassifiedAttribution,
  raw: AttributionInput,
  nowIso: string = new Date().toISOString()
): VisitorTouchState {
  const touch: TouchAttribution = {
    source: session.source,
    medium: session.medium,
    channel: session.channel,
    campaign: session.campaign,
    landingPage: raw.landingPath ?? null,
    referrer: raw.externalReferrer ?? null,
    utmSource: raw.utmSource ?? null,
    utmMedium: raw.utmMedium ?? null,
    utmCampaign: raw.utmCampaign ?? null,
    utmContent: raw.utmContent ?? null,
    utmTerm: raw.utmTerm ?? null,
    gclid: raw.gclid ?? null,
    fbclid: raw.fbclid ?? null,
    gbraid: raw.gbraid ?? null,
    wbraid: raw.wbraid ?? null,
    at: nowIso,
  };

  if (!existing) {
    return {
      first: touch,
      lastNonDirect: isDirectChannel(session.channel) ? null : touch,
      firstSeenAt: nowIso,
    };
  }

  const next: VisitorTouchState = {
    first: existing.first, // never overwrite
    lastNonDirect: existing.lastNonDirect,
    firstSeenAt: existing.firstSeenAt,
  };

  if (!isDirectChannel(session.channel)) {
    next.lastNonDirect = touch;
  }

  return next;
}

/**
 * If incoming referrer is self-referral/payment and we already have session attribution,
 * keep the existing classification (do not treat Stripe as acquisition).
 */
export function preserveAttributionOnSelfReferral(
  existing: ClassifiedAttribution | null,
  incomingReferrerHost: string | null | undefined
): ClassifiedAttribution | null {
  if (!existing) return null;
  if (shouldIgnoreReferrer(incomingReferrerHost)) return existing;
  return null;
}

export function touchFromClassified(
  classified: ClassifiedAttribution,
  raw?: AttributionInput,
  at?: string
): TouchAttribution {
  return {
    source: classified.source,
    medium: classified.medium,
    channel: classified.channel,
    campaign: classified.campaign,
    landingPage: raw?.landingPath ?? null,
    referrer: raw?.externalReferrer ?? null,
    utmSource: raw?.utmSource ?? null,
    utmMedium: raw?.utmMedium ?? null,
    utmCampaign: raw?.utmCampaign ?? null,
    utmContent: raw?.utmContent ?? null,
    utmTerm: raw?.utmTerm ?? null,
    gclid: raw?.gclid ?? null,
    fbclid: raw?.fbclid ?? null,
    gbraid: raw?.gbraid ?? null,
    wbraid: raw?.wbraid ?? null,
    at: at ?? new Date().toISOString(),
  };
}

export type BookingAttributionSnapshot = {
  first: TouchAttribution;
  last_non_direct: TouchAttribution | null;
  conversion: TouchAttribution;
  /** Verifierad nyhetsbrevs-touch (ingen mottagar-PII). */
  newsletter?: {
    campaign_id: string;
    link_key: string | null;
    utm_campaign: string | null;
    captured_at: string;
    attribution_quality: 'verified_direct' | 'estimated';
    model: string;
    window_days: number;
  };
};
